import React, { useState } from 'react';
import {
  SiReact,
  SiVuedotjs,
  SiAngular,
  SiNodedotjs,
  SiPython,
  SiPhp,
  SiDotnet,
  SiDocker,
  SiKubernetes,
  SiGooglecloud,
  SiPostgresql,
  SiMysql,
  SiMongodb,
  SiFirebase,
  SiRedis,
  SiSap,
  SiQuickbooks,
} from 'react-icons/si';
import { FaAws } from 'react-icons/fa';
import { TbBrandAzure } from 'react-icons/tb';
import { Plug, ScanText, Scale } from 'lucide-react';
import { Link } from 'react-router-dom';

const GROUPS = [
  {
    label: 'Lenguajes y frameworks',
    options: [
      { id: 'react', label: 'React', icon: SiReact },
      { id: 'vue', label: 'Vue', icon: SiVuedotjs },
      { id: 'angular', label: 'Angular', icon: SiAngular },
      { id: 'node', label: 'Node', icon: SiNodedotjs },
      { id: 'python', label: 'Python', icon: SiPython },
      { id: 'php', label: 'PHP', icon: SiPhp },
      { id: 'dotnet', label: '.NET', icon: SiDotnet },
    ],
  },
  {
    label: 'Nube e infraestructura',
    options: [
      { id: 'aws', label: 'AWS', icon: FaAws },
      { id: 'gcloud', label: 'Google Cloud', icon: SiGooglecloud },
      { id: 'azure', label: 'Azure', icon: TbBrandAzure },
      { id: 'docker', label: 'Docker', icon: SiDocker },
      { id: 'k8s', label: 'Kubernetes', icon: SiKubernetes },
    ],
  },
  {
    label: 'Bases de datos',
    options: [
      { id: 'postgresql', label: 'PostgreSQL', icon: SiPostgresql },
      { id: 'mysql', label: 'MySQL', icon: SiMysql },
      { id: 'mongodb', label: 'MongoDB', icon: SiMongodb },
      { id: 'firebase', label: 'Firebase', icon: SiFirebase },
      { id: 'redis', label: 'Redis', icon: SiRedis },
    ],
  },
  {
    label: 'Integraciones y APIs',
    options: [
      { id: 'rest-api', label: 'API REST', icon: Plug },
      { id: 'ocr', label: 'APIs de OCR', icon: ScanText },
      { id: 'sap', label: 'SAP', icon: SiSap },
      { id: 'quickbooks', label: 'Software contable', icon: SiQuickbooks },
      { id: 'legal-software', label: 'Software legal', icon: Scale },
    ],
  },
];

const ALL_OPTIONS = GROUPS.flatMap((g) => g.options);

// Recibe el trazo violeta que baja desde el showcase (mismo x: left-4) y, al
// entrar a fondo blanco, vuelve al índigo claro. Baja recto por ese borde y,
// ya en el fondo, cruza a la derecha hasta el 38% -- justo donde el Footer
// espera su entrada.
const StackBuilderCircuit = () => (
  <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden" aria-hidden="true">
    <div className="absolute left-4 top-0 bottom-0 w-[1.5px] bg-[#C7D2FE] opacity-70" />
    <div className="absolute left-4 bottom-0 w-[calc(38%-16px)] h-[1.5px] bg-[#C7D2FE] opacity-70" />
    <div className="absolute left-[10px] bottom-0 w-[11px] h-[11px] rounded-full bg-[#A5B4FC] opacity-90" />
  </div>
);

const joinLabels = (labels) => {
  if (labels.length === 0) return '';
  if (labels.length === 1) return labels[0];
  return `${labels.slice(0, -1).join(', ')} y ${labels[labels.length - 1]}`;
};

const StackBuilder = () => {
  const [selected, setSelected] = useState([]);

  const toggle = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));
  };

  const selectedLabels = ALL_OPTIONS.filter((o) => selected.includes(o.id)).map((o) => o.label);

  return (
    <section className="relative z-[1] bg-white px-5 sm:px-8 md:px-10 py-20 sm:py-28 overflow-hidden">
      <StackBuilderCircuit />
      <div className="relative z-10 max-w-3xl mx-auto text-center mb-12 sm:mb-14">
        <h2
          style={{ fontSize: 'clamp(26px, 4.2vw, 44px)', lineHeight: 1.15, fontWeight: 500, letterSpacing: '-0.01em' }}
          className="text-black mb-6"
        >
          Arma tu stack.
        </h2>
        <p className="text-black/60 text-[15px] sm:text-[16px] leading-relaxed max-w-xl mx-auto">
          Elige el lenguaje, la nube, la base de datos o el software que ya usas — te decimos que sí.
        </p>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto mb-10 sm:mb-12 flex flex-col gap-6">
        {GROUPS.map((group) => (
          <div key={group.label}>
            <p className="text-black/40 text-[11px] font-semibold uppercase tracking-[0.1em] mb-3">
              {group.label}
            </p>
            <div className="flex flex-wrap gap-3 sm:gap-4">
              {group.options.map(({ id, label, icon: Icon }) => {
                const isSelected = selected.includes(id);
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => toggle(id)}
                    aria-pressed={isSelected}
                    className={`flex items-center gap-2.5 pl-3 pr-4 py-2.5 rounded-full border transition-all duration-200 ${
                      isSelected
                        ? 'bg-black border-[#7C3AED] shadow-[0_0_16px_rgba(124,58,237,0.35)]'
                        : 'bg-black/5 border-black/10 hover:border-black/25 hover:bg-black/10'
                    }`}
                  >
                    <span
                      className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                        isSelected ? 'bg-white' : 'bg-black/5'
                      }`}
                    >
                      <Icon className={isSelected ? 'w-3.5 h-3.5 text-black' : 'w-3.5 h-3.5 text-black/40'} />
                    </span>
                    <span className={`text-[13px] sm:text-[14px] font-medium ${isSelected ? 'text-white' : 'text-black/50'}`}>
                      {label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="relative z-10 max-w-2xl mx-auto text-center min-h-[110px] flex flex-col items-center justify-center gap-6">
        {selectedLabels.length === 0 ? (
          <p className="text-black/40 text-[15px] sm:text-[16px]">
            Selecciona una o más opciones arriba para ver la respuesta.
          </p>
        ) : (
          <>
            <p
              style={{ fontSize: 'clamp(19px, 2.6vw, 26px)', lineHeight: 1.35, fontWeight: 500, letterSpacing: '-0.005em' }}
              className="text-black"
            >
              Perfecto. Trabajamos con <span className="text-[#7C3AED]">{joinLabels(selectedLabels)}</span> sin pedirte
              que migres nada.
            </p>
            <Link
              to="/diagnostico-gratuito"
              className="inline-flex items-center gap-2 bg-black text-white text-[14px] font-medium px-6 py-3 rounded-full hover:opacity-80 transition-opacity"
            >
              Hablemos de tu stack
              <span aria-hidden="true">→</span>
            </Link>
          </>
        )}
      </div>
    </section>
  );
};

export default StackBuilder;
